import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { UserDeviceIdentity } from '../interfaces';

/** localStorage key holding this browser's server-signed device id. */
const DEVICE_ID_KEY = 'userDeviceId';

/** `src/scripts/detectIncognito.min.js`, loaded as a global script by angular.json. */
declare function detectIncognito(): Promise<{ isPrivate: boolean }>;

/**
 * Owns this browser's device identity: a server-signed first-party id, an OSS FingerprintJS hash
 * used only for monitoring, and whether the session is private.
 *
 * Nothing here throws. Every failure degrades to "no value", because the server treats a missing
 * header as "use what you already know about this account".
 *
 * See `docs/user-device-graph.md` in smart-coolers-api for the design.
 */
@Injectable({
  providedIn: 'root'
})
export class UserDeviceService {
  private localStorage = inject(LocalStorageService);

  private visitorId: string | null = null;
  private hashPromise: Promise<string | null> | null = null;

  private privateMode: boolean | null = null;
  private privateModePromise: Promise<boolean | null> | null = null;

  /**
   * The signed device id this browser currently holds, or null when it has none. Never generated
   * locally — the server issues and signs it — so a missing value is normal on a first visit or
   * after a storage clear; call `storeIssuedId()` with what the API returns.
   */
  get deviceId(): string | null {
    // Guarded because this runs on every API request: where the browser refuses storage, reading
    // `localStorage` raises, and the throw would surface from `UserDeviceInterceptor.intercept()`
    // and fail the request rather than merely lose a header.
    try {
      const stored = this.localStorage.getItem(DEVICE_ID_KEY);
      return typeof stored === 'string' && stored.length > 0 ? stored : null;
    } catch {
      return null;
    }
  }

  /**
   * Persist the id the API issued. Stored `permanent` so a logout-driven `clear()` does not wipe
   * it, and never in SessionStorageService, which clears itself on `beforeunload`.
   *
   * A refused write costs the durability of this id, never the caller's flow: this runs immediately
   * before `createUser`, so a throw here would stop a registration.
   */
  storeIssuedId(deviceId: string): void {
    if (!deviceId) {
      return;
    }
    try {
      this.localStorage.setItem(DEVICE_ID_KEY, deviceId, true);
    } catch {
      // Nothing to recover: the server re-serves this account's id on the next request.
    }
  }

  /** Whether the stored value still looks like a signed id, so a tampered one can be re-fetched. */
  get hasPlausibleId(): boolean {
    const stored = this.deviceId;
    return !!stored && /^[0-9a-fA-F-]{36}(\.[0-9a-f]{32})?$/.test(stored);
  }

  /**
   * Report what is already held, synchronously. Called by the HTTP interceptor on every API
   * request, so it must never trigger a load: anything not yet computed reads as null, which the
   * server takes as "unknown" rather than as a negative.
   */
  peek(): UserDeviceIdentity {
    return { deviceId: this.deviceId, hash: this.visitorId, incognito: this.privateMode };
  }

  /**
   * Whether this is a private/incognito session, detected once and cached for the session.
   *
   * A per-request signal the device graph cannot express: `UserDevices.incognitoSeen` is sticky and
   * per-device, so it answers "has this browser ever been private", never "is this open private".
   *
   * Null when the heuristic is unavailable, and deliberately not `false`: "could not tell" and "not
   * private" are different facts, and only one of them is evidence.
   */
  async isPrivateMode(): Promise<boolean | null> {
    if (this.privateMode !== null) {
      return this.privateMode;
    }
    // Kept rather than cleared on settle, so an *unavailable* heuristic is cached too: a browser
    // the script does not cover is exactly the case where re-running per caller is useless.
    if (!this.privateModePromise) {
      this.privateModePromise = this.detectPrivateMode();
    }
    return this.privateModePromise;
  }

  private async detectPrivateMode(): Promise<boolean | null> {
    try {
      const result = await detectIncognito();
      this.privateMode = !!result?.isPrivate;
      return this.privateMode;
    } catch {
      return null;
    }
  }

  /**
   * Compute (or return) this browser's OSS FingerprintJS visitor id. Monitoring only: the server
   * can never block on it, because a client-side hash collides across identical phones. Resolves
   * to null rather than rejecting, so no caller has to handle a failure.
   */
  async hash(): Promise<string | null> {
    if (this.visitorId) {
      return this.visitorId;
    }
    // Kept on settle for the same reason as `isPrivateMode`: an agent that cannot load at all buys
    // nothing from retrying the dynamic import per caller.
    if (!this.hashPromise) {
      this.hashPromise = this.loadHash();
    }
    return this.hashPromise;
  }

  private async loadHash(): Promise<string | null> {
    try {
      // Imported lazily so the agent is not in the initial bundle.
      const FingerprintJS = await import('@fingerprintjs/fingerprintjs');
      const agent = await FingerprintJS.load();
      const result = await agent.get();
      this.visitorId = result.visitorId || null;
      return this.visitorId;
    } catch {
      return null;
    }
  }
}
